<script>
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    templates: {
      type: Array,
      default: () => [],
    },
    selectedTemplate: {
      type: Object,
      default: null,
    },
  },
  emits: ['select', 'edit', 'create'],
  methods: {
    getStatusClass(status) {
      const classes = {
        APPROVED: 'bg-n-teal-3 text-n-teal-11',
        PENDING: 'bg-n-amber-3 text-n-amber-11',
        REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
      };
      return classes[status] || 'bg-n-slate-3 text-n-slate-11';
    },
    isPending(template) {
      return template.status?.trim().toUpperCase() === 'PENDING';
    },
    getStatusLabel(status) {
      const key = `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.STATUS.${status?.toUpperCase()}`;
      const translated = this.$t(key);
      return translated !== key ? translated : status;
    },
    getCategoryLabel(category) {
      const key = `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CATEGORIES.${category?.toUpperCase()}`;
      const translated = this.$t(key);
      return translated !== key ? translated : category;
    },
    handleEditClick(template) {
      if (this.isPending(template)) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.PENDING_ALERT')
        );
        return;
      }
      this.$emit('edit', template);
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-y-auto p-2" style="scrollbar-width: thin">
    <!-- Empty State -->
    <div
      v-if="templates.length === 0"
      class="flex flex-col items-center justify-center py-8 px-4 text-center"
    >
      <fluent-icon icon="document" size="32" class="text-n-slate-11 mb-3" />
      <p class="text-sm text-n-slate-11 mb-3">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EMPTY_STATE') }}
      </p>
      <button
        class="text-sm text-n-brand underline hover:opacity-80"
        @click="$emit('create')"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTON') }}
      </button>
    </div>

    <!-- Template List -->
    <div class="space-y-2">
      <div
        v-for="template in templates"
        :key="template.id || template.name"
        class="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 bg-n-solid-1 border shadow-sm hover:shadow-md"
        :class="[
          selectedTemplate?.name === template.name
            ? 'border-n-brand ring-1 ring-n-brand/30'
            : 'border-n-weak hover:border-n-slate-6',
        ]"
        @click="$emit('select', template)"
      >
        <!-- Content -->
        <div class="flex-1 min-w-0">
          <!-- Name -->
          <h3
            class="text-sm font-medium text-n-slate-12 truncate"
            :title="template.name"
          >
            {{ template.name }}
          </h3>

          <!-- Meta -->
          <div class="flex items-center gap-2 mt-0.5">
            <!-- Status -->
            <span
              class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded"
              :class="getStatusClass(template.status)"
            >
              {{ getStatusLabel(template.status) }}
            </span>

            <!-- Category -->
            <span class="text-[10px] text-n-slate-11">
              {{ getCategoryLabel(template.category) }}
            </span>

            <!-- Language -->
            <span class="text-[10px] text-n-slate-11">
              {{ template.language }}
            </span>
          </div>
        </div>

        <!-- Edit Button -->
        <span
          v-if="isPending(template)"
          v-tooltip.top="
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.PENDING_TOOLTIP')
          "
          class="shrink-0 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity text-n-slate-6 cursor-not-allowed"
          @click.stop
        >
          <fluent-icon icon="edit" size="14" />
        </span>
        <button
          v-else
          v-tooltip.top="
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.BUTTON')
          "
          class="shrink-0 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity text-n-slate-11 hover:text-n-brand hover:bg-n-brand/10"
          @click.stop="handleEditClick(template)"
        >
          <fluent-icon icon="edit" size="14" />
        </button>
      </div>
    </div>
  </div>
</template>
